import React from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';

import Store from 'store';
import Session from 'store/session';

import { cubeItemAllowed } from 'logic/cube';
import { itemAllowed } from 'logic/item';
import { itemSetId } from 'logic/itemReducers';
import Data from 'data';

export function useDragDrop(type, dragSpec, dropSpec) {
  const [{ isDragging }, drag, dragPreview] = useDrag({
    item: {type},
    collect: monitor => ({isDragging: monitor.isDragging()}),
    ...dragSpec,
  });

  React.useEffect(() => {
    dragPreview(getEmptyImage(), { captureDraggingState: true });
  }, [dragPreview]);

  const [{ canDrop, isOver }, drop] = useDrop({
    accept: type,
    collect: monitor => ({canDrop: monitor.canDrop(), isOver: monitor.isOver()}),
    ...dropSpec,
  });

  let className = "";
  if (isDragging) {
    className = "d3p-dragging";
  } else if (canDrop) {
    if (isOver) {
      className = "d3p-drop-accept";
    } else {
      className = "d3p-drop-valid";
    }
  } else if (isOver) {
    className = "d3p-drop-invalid";
  }

  return [className, drag, drop];
}

function getItem(profile, stash, {location, slot}) {
  if (location === "equipment") {
    return profile.items?.[slot];
  } else if (location === "cube") {
    return profile.kanai?.[slot];
  } else if (location === "stash") {
    return stash?.[slot];
  }
}
function setProfileItem(profile, {location, slot}, item) {
  if (location === "equipment") {
    if (item && typeof item === "object") {
      profile.items[slot] = itemAllowed(profile, slot, item.id) ? item : null;
    } else if (item) {
      profile.items[slot] = itemAllowed(profile, slot, item) ? itemSetId(profile.items[slot], item, profile.class) : null;
    } else {
      profile.items[slot] = null;
    }
  } else if (location === "cube") {
    if (item && typeof item === "object") {
      item = item.id;
    }
    profile.kanai[slot] = item && cubeItemAllowed(profile, slot, item) ? item : null;
  }
}
function setStashItem(stash, {location, slot}, item, charClass) {
  if (location === "stash") {
    if (!item || typeof item === "object") {
      stash[slot] = item;
    } else if (item) {
      stash[slot] = itemSetId(stash[slot], item, charClass);
    }
  }
}

export function useItemMover() {
  const mainStore = Store.useStore();
  const sessionStore = Session.useStore();

  return React.useCallback((source, destination) => {
    if (!destination) return;
    if (source.location === destination.location && source.slot === destination.slot) return;

    const state = mainStore.getState();
    const profile = state.profiles[state.activeProfile];
    const stash = sessionStore.getState().stash;

    const sourceItem = getItem(profile, stash, source);
    const destinationItem = getItem(profile, stash, destination);
    const isCopy = destination.dropEffect === 'copy';

    if ((source.location !== "stash" && !isCopy) || destination.location !== "stash") {
      mainStore.dispatch.setProfile(profile => {
        profile = {...profile};

        if ((source.location === "equipment" && !isCopy) || destination.location === "equipment") {
          profile.items = {...profile.items};
        }
        if ((source.location === "cube" && !isCopy) || destination.location === "cube") {
          profile.kanai = {...profile.kanai};
        }

        setProfileItem(profile, destination, sourceItem);
        if (!isCopy) setProfileItem(profile, source, destinationItem);

        return profile;
      });
    }
    if (source.location === "stash" || destination.location === "stash") {
      sessionStore.dispatch.setStash(stash => {
        stash = [...stash];

        setStashItem(stash, destination, sourceItem, profile.class);
        if (!isCopy) setStashItem(stash, source, destinationItem, profile.class);

        return stash;
      });
    }
  }, [mainStore, sessionStore]);
}

export function useSkillMover() {
  const dispatch = Store.useDispatch();

  return React.useCallback((source, destination) => {
    if (!destination) return;
    if (source.index === destination.index) return;

    dispatch.setProfile(profile => {
      profile = {...profile};

      const sourceSkill = profile.skills[source.index];
      const destinationSkill = profile.skills[destination.index];
      if (sourceSkill && destination.index === 0 && Data.skills[profile.class][sourceSkill[0]]?.nolmb) return profile;
      if (destinationSkill && source.index === 0 && Data.skills[profile.class][destinationSkill[0]]?.nolmb) return profile;

      profile.skills = profile.skills.slice();
      profile.skills[source.index] = destinationSkill;
      profile.skills[destination.index] = sourceSkill;

      return profile;
    });
  }, [dispatch]);
}

export function usePassiveMover() {
  const dispatch = Store.useDispatch();

  return React.useCallback((source, destination) => {
    if (!destination) return;
    if (source.index === destination.index) return;

    dispatch.setProfile(profile => {
      profile = {...profile};

      const sourcePassive = profile.passives[source.index];
      const destinationPassive = profile.passives[destination.index];

      profile.passives = profile.passives.slice();
      profile.passives[source.index] = destinationPassive;
      profile.passives[destination.index] = sourcePassive;

      return profile;
    });
  }, [dispatch]);
}
